import { useSiteDashboard } from '@/hooks/useSiteDashboard';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { X, ExternalLink } from 'lucide-react';
import { BottomSheet, KpiCard, RevenueChart, RevenuePeriodPill } from '@evi/ui';
import type { LocationSummaryData, OperatorDashboardPeriod } from '@evi/shared';
import { useLocationSummary } from '@/hooks/useLocationSummary';
import { useViewport } from '@/hooks/useViewport';
import { formatCurrency, formatEnergy } from '@/pages/dashboard/widgets/_lib';
import styles from './LocationInfoCard.module.css';

export interface InfoCardLocation {
  _id: string;
  name: string;
  chargerCount: number;
  onlineChargerCount: number;
}

interface LocationInfoCardProps {
  operationalOnly?: boolean;
  location: InfoCardLocation;
  onClose: () => void;
  onOpenDetail: (id: string) => void;
}

/**
 * Info panel for a selected map location. Two presentations:
 *
 *  desktop (>=1024px)
 *    motion.div slide-in panel pinned to the right edge of the
 *    .mapBackground viewport, spring-animated. ESC + click-outside
 *    dismissal handled inline.
 *
 *  mobile / tablet (<1024px)
 *    Rendered through @evi/ui's BottomSheet, which portals to
 *    document.body to escape the .mapBackground stacking context
 *    (z-index: 0 inside .page's z-index: 2 context). BottomSheet
 *    owns its own dismissal surfaces (X button in header, ESC,
 *    backdrop tap, swipe-down) plus focus trap + body-scroll lock.
 *
 * Content is identical at both presentations — status badge,
 * online/total stat tiles, descriptive note, "Open location
 * detail" CTA. Only the wrapper container changes.
 *
 * Originally shipped (Commits 2/3) as the desktop-only panel
 * with an @media (max-width: 768px) CSS rewrite to a bottom-
 * sheet shape. That CSS-only swap suffered the .mapBackground
 * stacking-context trap (LocationInfoCard z-index 8 inside
 * .mapBackground z-index 0 = below .page widgets at z-index 2),
 * which made the card unreachable on mobile. Discovery report
 * recommended the BottomSheet port as the cleanest fix; this
 * commit lands it.
 */
export function LocationInfoCard({ location, onClose, onOpenDetail, operationalOnly = false }: LocationInfoCardProps) {
  const isSite = useSiteDashboard();
  const viewport = useViewport();
  const isNarrow = viewport === 'mobile' || viewport === 'tablet';

  const status = deriveStatus(location);

  // Period drives both the embedded RevenueChart's bucket array and
  // the summary refetch. KPI fields are window-independent
  // (revenueToday is always today, energyMtd always month-to-date,
  // chargers/active sessions are point-in-time), so React Query's
  // `placeholderData: keepPreviousData` behaviour inside the hook
  // means the KPI tiles don't flicker across period flips — they
  // keep their previous values while the new bucket array fetches.
  const [period, setPeriod] = useState<OperatorDashboardPeriod>('7d');
  const summaryQ = useLocationSummary(operationalOnly ? null : location?._id ?? null, period);

  // Shared content used by both presentations. Status badge,
  // descriptive note, KPI 2x2 grid, and "Open location detail" CTA
  // render the same way regardless of wrapper. The older 2-up
  // ONLINE / TOTAL CHARGERS stat tiles were dropped in this commit
  // (Path A from the spec) — the new "Chargers Online" KPI tile
  // carries the same number with the same online-of-total framing
  // plus a faulted-count delta when applicable, so the legacy tiles
  // were redundant.
  const statusBadge = (
    <span className={`${styles.statusBadge} ${statusClass(status)}`}>
      <span className={styles.statusDot} />
      {statusLabel(status)}
    </span>
  );

  const note = (
    <p className={styles.note}>
      {operationalOnly ? 'Live charger status for this location in your sales book.' : isSite ? 'Charging sales are what drivers paid, including EVI fees. Open My Sites for this site’s chargers and settings.' : 'Open the location detail for active sessions, today’s revenue, address, and per-charger status.'}
    </p>
  );

  // KPI 2x2 grid. Top row financial (Revenue Today / Energy MTD),
  // bottom row operational (Chargers Online / Active Sessions).
  // Same KpiCard component the main dashboard uses; loading + error
  // states are inline so the rest of the card (title, note, CTA)
  // stays interactive while stats settle.
  const kpiGrid = operationalOnly ? (
    <div className={styles.kpiGrid}>
      <KpiCard label="Chargers" value={String(location.chargerCount)} />
      <KpiCard label="Online" value={String(location.onlineChargerCount)} />
    </div>
  ) : (
    <div className={styles.kpiGrid}>
      {summaryQ.isError && (
        <div className={styles.kpiError}>Couldn&rsquo;t load location stats.</div>
      )}
      {!summaryQ.isError && (
        <>
          <KpiCard
            label={isSite ? 'Charging sales today' : 'Revenue Today'}
            value={kpiValue(summaryQ.data, (s) => formatCurrency(s.revenueToday))}
          />
          <KpiCard
            label="Energy MTD"
            value={kpiValue(summaryQ.data, (s) => formatEnergy(s.energyMtd))}
          />
          <KpiCard
            label="Chargers Online"
            value={kpiValue(
              summaryQ.data,
              (s) => `${s.chargersOnline} / ${s.totalChargers}`,
            )}
            delta={
              summaryQ.data && summaryQ.data.faultedCount > 0
                ? { value: `${summaryQ.data.faultedCount} faulted`, direction: 'down' }
                : undefined
            }
          />
          <KpiCard
            label="Active Sessions"
            value={kpiValue(
              summaryQ.data,
              (s) => `${s.activeSessions} / ${s.totalSessionsToday}`,
            )}
            meta={summaryQ.data ? 'today' : undefined}
          />
        </>
      )}
    </div>
  );

  // Revenue chart slot — period chip row + bucketed series + custom
  // footer pill. Hidden while the first summary fetch is in flight
  // (no buckets to show); kept visible across period flips via the
  // hook's keepPreviousData behaviour. Also hidden on error since
  // the .kpiError pill already informs the operator.
  const chartSlot = !operationalOnly && summaryQ.data && !summaryQ.isError ? (
    <div className={styles.chartSlot}>
      <RevenueChart
        titleLabel={isSite ? 'CHARGING SALES: USD' : undefined}
        data={summaryQ.data.revenueLast24h}
        currentRate={0}
        period={period}
        onPeriodChange={setPeriod}
        allowedPeriods={['24h', '7d', '30d']}
        showRate={false}
        footer={
          <RevenuePeriodPill
            label="TOTAL"
            amount={sumBuckets(summaryQ.data.revenueLast24h)}
            sessionCount={summaryQ.data.sessionCountWindow}
          />
        }
      />
    </div>
  ) : null;

  const cta = (
    <button
      type="button"
      className={styles.cta}
      onClick={() => onOpenDetail(location._id)}
    >
      {isSite ? 'Open My Sites' : 'Open location detail'}
      <ExternalLink size={14} />
    </button>
  );

  if (isNarrow) {
    return (
      <SheetPresentation
        title={location.name}
        onClose={onClose}
        statusBadge={statusBadge}
        note={note}
        kpiGrid={kpiGrid}
        chartSlot={chartSlot}
        cta={cta}
      />
    );
  }

  return (
    <PanelPresentation
      location={location}
      onClose={onClose}
      statusBadge={statusBadge}
      note={note}
      kpiGrid={kpiGrid}
      chartSlot={chartSlot}
      cta={cta}
    />
  );
}

/**
 * Sum the bucket array client-side rather than exposing windowTotal
 * from the API. The server already returned the buckets and the
 * session count; a separate window-sum field would be redundant. The
 * bucket array length is small (24 / 7 / 30) so the sum is trivially
 * cheap.
 */
function sumBuckets(buckets: number[]): number {
  let total = 0;
  for (const v of buckets) total += v;
  // Round to 2 decimal places to avoid floating-point summation
  // artifacts surfacing as "$36.94000000001" in the footer copy.
  return Math.round(total * 100) / 100;
}

/**
 * Render-prop helper for KPI values that fall back to a placeholder
 * while the summary query is in flight. Centralised so all four
 * tiles use the same em-dash convention and skip the formatting
 * call when data is undefined (formatCurrency / formatEnergy would
 * otherwise NaN on undefined input).
 */
function kpiValue(
  data: LocationSummaryData | undefined,
  format: (s: LocationSummaryData) => string,
): string {
  return data ? format(data) : '—';
}

/**
 * Desktop presentation — right-edge slide-in panel with custom
 * ESC + click-outside dismissal. BottomSheet's behavior isn't
 * appropriate here (a centered modal at desktop breakpoint would
 * lose the map context); the panel design from Commits 2/3 stays.
 */
function PanelPresentation({
  location, onClose, statusBadge, note, kpiGrid, chartSlot, cta,
}: {
  location: InfoCardLocation;
  onClose: () => void;
  statusBadge: ReactNode;
  note: ReactNode;
  kpiGrid: ReactNode;
  chartSlot: ReactNode;
  cta: ReactNode;
}) {
  const cardRef = useRef<HTMLDivElement | null>(null);

  // ESC closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Click-outside closes — but marker clicks are excluded
  // (the parent already swaps the selected location, which
  // updates the card in-place; closing here would race that
  // update and cause a flicker).
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (cardRef.current?.contains(target)) return;
      // Skip if the click landed on a marker — markers carry the
      // stable `.location-marker-wrapper` class set in
      // LocationMarker.tsx.
      if (target instanceof HTMLElement && target.closest('.location-marker-wrapper')) {
        return;
      }
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  return (
    <motion.div
      ref={cardRef}
      className={styles.card}
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 28, stiffness: 200 }}
      role="dialog"
      aria-label={`${location.name} details`}
    >
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className={styles.name}>{location.name}</h3>
          <div className={styles.statusRow}>{statusBadge}</div>
        </div>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>

      {/* Body is the scroll container — flex:1 + overflow-y:auto in
          the module CSS. Note + KPI grid + RevenueChart + CTA all
          flow inside in vertical order. The sticky-footer wrapper
          that previously pinned the CTA was dropped in Phase 2;
          chart slot inserted between KPI grid and CTA in Phase 3. */}
      <div className={styles.body}>
        {note}
        {kpiGrid}
        {chartSlot}
        {cta}
      </div>
    </motion.div>
  );
}

/**
 * Mobile/tablet presentation — through @evi/ui BottomSheet which
 * portals to document.body. BottomSheet owns title (location
 * name), close button, ESC, backdrop dismiss, swipe-down, focus
 * trap, body scroll lock. We just provide the body content:
 * status badge + stats + note + CTA in vertical order.
 */
function SheetPresentation({
  title, onClose, statusBadge, note, kpiGrid, chartSlot, cta,
}: {
  title: string;
  onClose: () => void;
  statusBadge: ReactNode;
  note: ReactNode;
  kpiGrid: ReactNode;
  chartSlot: ReactNode;
  cta: ReactNode;
}) {
  return (
    <BottomSheet isOpen onClose={onClose} title={title}>
      <div className={styles.sheetBody}>
        <div className={styles.sheetStatusRow}>{statusBadge}</div>
        {note}
        {kpiGrid}
        {chartSlot}
        <div className={styles.sheetCta}>{cta}</div>
      </div>
    </BottomSheet>
  );
}

type LocationStatus = 'online' | 'partial' | 'faulted' | 'idle';

function deriveStatus(loc: InfoCardLocation): LocationStatus {
  if (loc.chargerCount === 0) return 'idle';
  if (loc.onlineChargerCount === 0) return 'faulted';
  if (loc.onlineChargerCount < loc.chargerCount) return 'partial';
  return 'online';
}

function statusClass(s: LocationStatus): string {
  switch (s) {
    case 'online':  return styles.statusBadgeOnline ?? '';
    case 'partial': return styles.statusBadgeOffline ?? '';
    case 'faulted': return styles.statusBadgeFaulted ?? '';
    case 'idle':    return styles.statusBadgeIdle ?? '';
  }
}

function statusLabel(s: LocationStatus): string {
  switch (s) {
    case 'online':  return 'All online';
    case 'partial': return 'Partially online';
    case 'faulted': return 'Faulted';
    case 'idle':    return 'No chargers';
  }
}
